(function () {
  const SELECTORS = {
    root: '#cstm-countdown-timer',
    message: '#cstm-countdown-message',
    display: '#cstm-countdown-display',
    endBox: '#cstm-countdown-end',
    days: '#days',
    hours: '#hours',
    minutes: '#minutes',
    seconds: '#seconds',
    daysLabel: '#days-label',
    hoursLabel: '#hours-label',
    minutesLabel: '#minutes-label',
    secondsLabel: '#seconds-label',
  };

  const UNIT_KEYS = ['days', 'hours', 'minutes', 'seconds'];

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  function sanitizeHTML(html) {
    // Basic HTML sanitizer: allow common tags/attrs used in simple messages/links
    const allowedTags = new Set(['p', 'a', 'strong', 'em', 'span', 'br', 'ul', 'ol', 'li']);
    const allowedAttrs = new Set(['href', 'target', 'rel', 'class']);
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const walker = document.createTreeWalker(tmp, NodeFilter.SHOW_ELEMENT, null, false);
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (!allowedTags.has(node.tagName.toLowerCase())) {
        node.replaceWith(...Array.from(node.childNodes));
      } else {
        for (const attr of [...node.attributes]) {
          if (!allowedAttrs.has(attr.name)) node.removeAttribute(attr.name);
        }
        if (node.tagName.toLowerCase() === 'a') {
          node.setAttribute('rel', 'noopener noreferrer');
          if (!node.getAttribute('target')) node.setAttribute('target', '_blank');
        }
      }
    }
    return tmp.innerHTML;
  }

  function parseDateTime(input, timeZone, customOffset) {
    // Expected format: DD/MM/YYYY HH:MM (24h)
    const parts = input.trim().split(/\s+/);
    if (parts.length !== 2) throw new Error('Invalid date/time format. Expected DD/MM/YYYY HH:MM');

    const [datePart, timePart] = parts;

    // Date: DD/MM/YYYY
    const dateMatch = datePart.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!dateMatch) throw new Error('Invalid date format. Expected DD/MM/YYYY');

    const [, dd, mm, yyyy] = dateMatch;
    const day = parseInt(dd, 10);
    const month = parseInt(mm, 10) - 1; // 0-based
    const year = parseInt(yyyy, 10);

    // Time: HH:MM
    const timeMatch = timePart.match(/^(\d{2}):(\d{2})$/);
    if (!timeMatch) throw new Error('Invalid time format. Expected HH:MM (24h)');
    const [, hh, min] = timeMatch;
    const hours = parseInt(hh, 10);
    const minutes = parseInt(min, 10);

    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      throw new Error('Hours/minutes out of range.');
    }

    // Build local date
    const localDate = new Date(year, month, day, hours, minutes, 0, 0);

    // Convert to UTC based on timeZone
    if (timeZone === 'utc') {
      return new Date(Date.UTC(year, month, day, hours, minutes, 0, 0));
    }

    if (timeZone === 'custom') {
      if (!customOffset || !/^[+-]\d{2}:\d{2}$/.test(customOffset)) {
        throw new Error('Invalid custom time offset. Expected +HH:MM or -HH:MM (e.g., +05:30)');
      }
      const sign = customOffset[0] === '-' ? -1 : 1;
      const [oh, om] = customOffset.slice(1).split(':').map(s => parseInt(s, 10));
      if (oh < 0 || oh > 14 || om < 0 || om > 59) {
        throw new Error('Custom offset hours/minutes out of range.');
      }
      const offsetMs = sign * ((oh * 60 + om) * 60 * 1000);
      const utcTime = localDate.getTime() - offsetMs;
      return new Date(utcTime);
    }

    // 'store' → treat the entered time as local to the store/browser context (the natural interpretation)
    return localDate;
  }

  function getSettings(root) {
    return {
      enable: (root.dataset.enable || 'true').toLowerCase() === 'true',
      timerMode: root.dataset.timerMode || 'end_only',
      startTimeStr: root.dataset.startTime || '',
      endTimeStr: root.dataset.endTime || '',
      timeZone: root.dataset.timeZone || 'store',
      customOffset: root.dataset.customOffset || '',
      displayUnits: root.dataset.displayUnits || 'days_hours_mins_secs',
      showLabels: (root.dataset.showLabels || 'true').toLowerCase() === 'true',
      onTimerEnd: root.dataset.onTimerEnd || 'hide',
      endMessage: root.dataset.endMessage || 'The offer has ended.',
      preStartMessage: root.dataset.preStartMessage || 'Starts in:',
      duringMessage: root.dataset.duringMessage || 'Ends in:',
      replaceContent: root.dataset.replaceContent || '',
    };
  }

  function applyDisplayUnits(display, displayUnits) {
    const units = {
      days: display.querySelector('.cstm-days'),
      hours: display.querySelector('.cstm-hours'),
      minutes: display.querySelector('.cstm-minutes'),
      seconds: display.querySelector('.cstm-seconds'),
    };

    // Show/hide by displayUnits
    if (displayUnits === 'mins_secs') {
      units.days.style.display = 'none';
      units.hours.style.display = 'none';
      units.minutes.style.display = '';
      units.seconds.style.display = '';
    } else if (displayUnits === 'hours_mins_secs') {
      units.days.style.display = 'none';
      units.hours.style.display = '';
      units.minutes.style.display = '';
      units.seconds.style.display = '';
    } else {
      // days_hours_mins_secs
      units.days.style.display = '';
      units.hours.style.display = '';
      units.minutes.style.display = '';
      units.seconds.style.display = '';
    }
  }

  function applyLabels(display, showLabels) {
    const labels = {
      days: display.querySelector('#days-label'),
      hours: display.querySelector('#hours-label'),
      minutes: display.querySelector('#minutes-label'),
      seconds: display.querySelector('#seconds-label'),
    };

    for (const key of UNIT_KEYS) {
      if (labels[key]) labels[key].style.display = showLabels ? '' : 'none';
    }
  }

  function renderTimer(root, remaining, displayUnits) {
    const daysEl = root.querySelector(SELECTORS.days);
    const hoursEl = root.querySelector(SELECTORS.hours);
    const minutesEl = root.querySelector(SELECTORS.minutes);
    const secondsEl = root.querySelector(SELECTORS.seconds);

    const totalSeconds = Math.max(remaining, 0);

    const days = Math.floor(totalSeconds / (24 * 60 * 60));
    const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    if (daysEl) daysEl.textContent = pad(days);
    if (hoursEl) hoursEl.textContent = pad(hours);
    if (minutesEl) minutesEl.textContent = pad(minutes);
    if (secondsEl) secondsEl.textContent = pad(seconds);
  }

  function handleEnd(root, settings) {
    const messageEl = root.querySelector(SELECTORS.message);
    const displayEl = root.querySelector(SELECTORS.display);
    const endBox = root.querySelector(SELECTORS.endBox);

    if (settings.onTimerEnd === 'hide') {
      root.style.display = 'none';
      return;
    }

    if (settings.onTimerEnd === 'show_message') {
      displayEl.hidden = true;
      if (messageEl) messageEl.hidden = true;
      endBox.hidden = false;
      endBox.textContent = settings.endMessage || 'The offer has ended.';
      return;
    }

    if (settings.onTimerEnd === 'replace_content') {
      displayEl.hidden = true;
      if (messageEl) messageEl.hidden = true;
      endBox.hidden = false;
      endBox.innerHTML = sanitizeHTML(settings.replaceContent || '<p>Offer ended.</p>');
      return;
    }
  }

  function initCountdown(root) {
    if (!root) return;

    const settings = getSettings(root);

    if (!settings.enable) {
      root.style.display = 'none';
      return;
    }

    const messageEl = root.querySelector(SELECTORS.message);
    const displayEl = root.querySelector(SELECTORS.display);
    const endBox = root.querySelector(SELECTORS.endBox);

    // Apply display units + labels
    applyDisplayUnits(displayEl, settings.displayUnits);
    applyLabels(displayEl, settings.showLabels);

    let startTime = null;
    let endTime = null;

    // Parse times
    try {
      if (settings.timerMode === 'start_and_end') {
        if (!settings.startTimeStr) throw new Error('Start time is required when Timer Mode is "Show Starts in then Ends in".');
        startTime = parseDateTime(settings.startTimeStr, settings.timeZone, settings.customOffset);
      }

      if (!settings.endTimeStr) throw new Error('End time is required.');
      endTime = parseDateTime(settings.endTimeStr, settings.timeZone, settings.customOffset);
    } catch (err) {
      console.error('[cstm_countdown_timer] Invalid configuration:', err.message);
      root.innerHTML = `<div class="cstm-countdown-error" role="alert">Countdown configuration error: ${err.message}</div>`;
      return;
    }

    const now = () => Date.now();

    let currentTarget = null; // 'start' | 'end'
    let targetTime = null;

    function updatePhase() {
      const current = now();

      if (settings.timerMode === 'start_and_end') {
        if (current < startTime.getTime()) {
          currentTarget = 'start';
          targetTime = startTime.getTime();
          if (messageEl) messageEl.textContent = settings.preStartMessage || 'Starts in:';
        } else if (current < endTime.getTime()) {
          currentTarget = 'end';
          targetTime = endTime.getTime();
          if (messageEl) messageEl.textContent = settings.duringMessage || 'Ends in:';
        } else {
          handleEnd(root, settings);
          return false; // stop loop
        }
      } else {
        // end_only
        if (current < endTime.getTime()) {
          currentTarget = 'end';
          targetTime = endTime.getTime();
          if (messageEl) messageEl.textContent = settings.duringMessage || 'Ends in:';
        } else {
          handleEnd(root, settings);
          return false; // stop loop
        }
      }
      return true; // continue
    }

    function tick() {
      if (!updatePhase()) return;

      const current = now();
      const remaining = Math.floor((targetTime - current) / 1000);

      if (remaining <= 0) {
        // phase changed or ended
        if (!updatePhase()) return;
        return tick();
      }

      renderTimer(root, remaining, settings.displayUnits);

      // Determine update cadence
      const hasSeconds = settings.displayUnits !== 'mins_secs' ? true : true; // always update every second for precision
      const nextDelay = hasSeconds ? 1000 : 60000;

      setTimeout(tick, nextDelay);
    }

    // Initial render + start
    if (!updatePhase()) return;
    tick();
  }

  document.addEventListener('DOMContentLoaded', function () {
    const timers = document.querySelectorAll(SELECTORS.root);
    timers.forEach((timer) => initCountdown(timer));
  });
})();